import axios from 'axios';

// Use relative URL for production (nginx proxy), or localhost for development
// In production, nginx proxies /api to the backend, so we use relative URLs
// This ensures API calls use the same protocol (HTTP/HTTPS) as the page
// Always use relative URL to avoid SSL protocol errors
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production, always use relative URL
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  // In development, use localhost
  return 'http://localhost:8000/api';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized) - redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Houses API - paths don't include /api prefix since baseURL handles it
export const housesAPI = {
  getAll: () => api.get('/houses'),
  getById: (id) => api.get(`/houses/${id}`),
  create: (data) => api.post('/houses', data),
  update: (id, data) => api.put(`/houses/${id}`, data),
  delete: (id) => api.delete(`/houses/${id}`),
};

// Categories API
export const categoriesAPI = {
  getAll: (houseId) => {
    const params = {};
    if (houseId) params.house_id = houseId;
    return api.get('/categories', { params });
  },
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Items API
export const itemsAPI = {
  getAll: (houseId, categoryId) => {
    const params = {};
    if (houseId) params.house_id = houseId;
    if (categoryId) params.category_id = categoryId;
    return api.get('/items', { params });
  },
  getById: (id) => api.get(`/items/${id}`),
  create: (data) => api.post('/items', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
  getLowStock: (houseId) => {
    const params = {};
    if (houseId) params.house_id = houseId;
    return api.get('/items/low-stock', { params });
  },
  getExpiring: (houseId, days = 30) => {
    const params = { days };
    if (houseId) params.house_id = houseId;
    return api.get('/items/expiring', { params });
  },
};

// Alerts API
export const alertsAPI = {
  getAll: (days = 30) => api.get(`/alerts?days=${days}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (houseId) => {
    const params = {};
    if (houseId) params.house_id = houseId;
    return api.get('/dashboard/stats', { params });
  },
};

// Images API
export const imagesAPI = {
  upload: (itemId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/items/${itemId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (id) => api.delete(`/images/${id}`),
};

// Notes API
export const notesAPI = {
  getAll: (houseId) => {
    const params = {};
    if (houseId) params.house_id = houseId;
    return api.get('/notes', { params });
  },
  getById: (id) => api.get(`/notes/${id}`),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.put(`/notes/${id}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
};

// Groceries API
export const groceriesAPI = {
  getAll: (houseId) => {
    const params = {};
    if (houseId) params.house_id = houseId;
    return api.get('/groceries', { params });
  },
  getById: (id) => api.get(`/groceries/${id}`),
  create: (data) => api.post('/groceries', data),
  update: (id, data) => api.put(`/groceries/${id}`, data),
  delete: (id) => api.delete(`/groceries/${id}`),
  toggle: (id) => api.post(`/groceries/${id}/toggle`),
  addToStock: (groceryIds, defaultCategoryId) => {
    return api.post('/groceries/add-to-stock', {
      grocery_ids: groceryIds,
      default_category_id: defaultCategoryId || null,
    });
  },
};

// Suggestions API
export const suggestionsAPI = {
  getSuggestions: (query, houseId, limit = 10) => {
    const params = { q: query, limit };
    if (houseId) params.house_id = houseId;
    return api.get('/items/suggestions', { params });
  },
};

// Notifications API
export const notificationsAPI = {
  subscribe: (subscription) => api.post('/notifications/subscribe', subscription),
  unsubscribe: (subscriptionId) => api.delete(`/notifications/subscribe/${subscriptionId}`),
  unsubscribeByEndpoint: (endpoint) => api.delete('/notifications/subscribe-by-endpoint', { params: { endpoint } }),
  getSubscriptions: (houseId) => {
    const params = {};
    if (houseId) params.house_id = houseId;
    return api.get('/notifications/subscriptions', { params });
  },
  getVapidPublicKey: () => api.get('/notifications/vapid-public-key'),
  test: (houseId) => {
    const params = {};
    if (houseId) params.house_id = houseId;
    return api.post('/notifications/test', null, { params });
  },
};

// Budget API
export const budgetAPI = {
  // Budget Items
  getBudgetItems: (houseId, isActive) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (isActive !== undefined) params.is_active = isActive;
    return api.get('/budget/items', { params });
  },
  getBudgetItem: (id) => api.get(`/budget/items/${id}`),
  createBudgetItem: (data) => api.post('/budget/items', data),
  updateBudgetItem: (id, data) => api.put(`/budget/items/${id}`, data),
  deleteBudgetItem: (id) => api.delete(`/budget/items/${id}`),
  
  // Loans
  getLoans: (houseId, isActive) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (isActive !== undefined) params.is_active = isActive;
    return api.get('/budget/loans', { params });
  },
  getLoan: (id) => api.get(`/budget/loans/${id}`),
  createLoan: (data) => api.post('/budget/loans', data),
  updateLoan: (id, data) => api.put(`/budget/loans/${id}`, data),
  deleteLoan: (id) => api.delete(`/budget/loans/${id}`),
  
  // People
  getBudgetPeople: (houseId) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    return api.get('/budget/people', { params });
  },
  getBudgetPerson: (id) => api.get(`/budget/people/${id}`),
  createBudgetPerson: (data) => api.post('/budget/people', data),
  updateBudgetPerson: (id, data) => api.put(`/budget/people/${id}`, data),
  deleteBudgetPerson: (id) => api.delete(`/budget/people/${id}`),
  
  // Savings
  getSavingsCategories: (houseId, isActive) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (isActive !== undefined) params.is_active = isActive;
    return api.get('/budget/savings', { params });
  },
  getSavingsCategory: (id) => api.get(`/budget/savings/${id}`),
  createSavingsCategory: (data) => api.post('/budget/savings', data),
  updateSavingsCategory: (id, data) => api.put(`/budget/savings/${id}`, data),
  deleteSavingsCategory: (id) => api.delete(`/budget/savings/${id}`),
  
  // Transactions (read-only now, auto-created from bank statements)
  getTransactions: (houseId, budgetItemId, transactionType, startDate, endDate) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (budgetItemId !== undefined) params.budget_item_id = budgetItemId;
    if (transactionType) params.transaction_type = transactionType;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/budget/transactions', { params });
  },
  getTransaction: (transactionId) => api.get(`/budget/transactions/${transactionId}`),
  updateTransaction: (transactionId, data) => api.put(`/budget/transactions/${transactionId}`, data),
  createDefaultBudgetItems: (houseId) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    return api.post('/budget/setup/default-budget-items', null, { params });
  },
  reCategorizeTransactions: (houseId, overwriteExisting = false) => {
    const params = { overwrite_existing: overwriteExisting };
    if (houseId !== undefined) params.house_id = houseId;
    return api.post('/budget/transactions/re-categorize', null, { params });
  },
  getTransaction: (id) => api.get(`/budget/transactions/${id}`),
  
  // Bank Statements
  uploadBankStatements: (houseId, files) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    return api.post('/budget/bank-statements/upload', formData, {
      params,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getBankStatements: (houseId) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    return api.get('/budget/bank-statements', { params });
  },
  deleteBankStatement: (statementId) => api.delete(`/budget/bank-statements/${statementId}`),

  // PayPal CSV (for spending report merchants)
  uploadPaypalCsv: (houseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/budget/paypal/upload', formData, {
      params: { house_id: houseId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getPaypalStatus: (houseId) => api.get('/budget/paypal', { params: { house_id: houseId } }),
  deletePaypalCsv: (houseId) => api.delete('/budget/paypal', { params: { house_id: houseId } }),

  // Consolidated HTML spending report (LCL + Bourso + PayPal)
  generateSpendingReport: (houseId) =>
    api.post('/budget/analysis/spending-report', null, {
      params: { house_id: houseId },
      responseType: 'text',
      headers: { Accept: 'text/html' },
    }),
  
  // Analysis Endpoints
  getSpendingOverTime: (houseId, startDate, endDate) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/budget/analysis/spending-over-time', { params });
  },
  getIncomeVsExpenses: (houseId, period, startDate, endDate) => {
    const params = { period: period || 'monthly' };
    if (houseId !== undefined) params.house_id = houseId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/budget/analysis/income-vs-expenses', { params });
  },
  getCategoryBreakdown: (houseId, startDate, endDate) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/budget/analysis/category-breakdown', { params });
  },
  getMonthlySummary: (houseId, year) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (year) params.year = year;
    return api.get('/budget/analysis/monthly-summary', { params });
  },
  getBalanceTrend: (houseId, startDate, endDate) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/budget/analysis/balance-trend', { params });
  },
  
  // Calculations
  getSummary: (houseId) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    return api.get('/budget/summary', { params });
  },
  getPersonCalculations: (houseId) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    return api.get('/budget/person-calculations', { params });
  },
  getReport: (startDate, endDate, houseId) => {
    const params = { start_date: startDate, end_date: endDate };
    if (houseId !== undefined) params.house_id = houseId;
    return api.get('/budget/report', { params });
  },
};

// Projects API
export const projectsAPI = {
  // Project CRUD
  getAll: (houseId, status) => {
    const params = {};
    if (houseId !== undefined) params.house_id = houseId;
    if (status) params.status = status;
    return api.get('/projects', { params });
  },
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  
  // File operations
  uploadFile: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getFiles: (projectId) => api.get(`/projects/${projectId}/files`),
  downloadFile: (fileId) => {
    return api.get(`/projects/files/${fileId}`, {
      responseType: 'blob',
    });
  },
  deleteFile: (fileId) => api.delete(`/projects/files/${fileId}`),
  
  // Expense operations
  getExpenses: (projectId) => api.get(`/projects/${projectId}/expenses`),
  createExpense: (projectId, data) => api.post(`/projects/${projectId}/expenses`, data),
  updateExpense: (id, data) => api.put(`/projects/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/projects/expenses/${id}`),
  
  // Budget summary
  getBudgetSummary: (projectId) => api.get(`/projects/${projectId}/budget-summary`),
  
  // Notes/Checklist operations
  getNotes: (projectId) => api.get(`/projects/${projectId}/notes`),
  createNote: (projectId, data) => api.post(`/projects/${projectId}/notes`, data),
  updateNote: (id, data) => api.put(`/projects/notes/${id}`, data),
  deleteNote: (id) => api.delete(`/projects/notes/${id}`),
  toggleNote: (id) => api.post(`/projects/notes/${id}/toggle`),
};

// Auth API
export const authAPI = {
  login: (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    return api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getMe: () => api.get('/auth/me'),
};

export default api;

// Shared lists API (owner side, requires login)
export const sharedListsAPI = {
  getAll: () => api.get('/shared-lists'),
  create: (data) => api.post('/shared-lists', data),
  delete: (id) => api.delete(`/shared-lists/${id}`),
};

// Public lists API (no login, scoped by share token)
export const publicListsAPI = {
  get: (token) => api.get(`/public/lists/${token}`),
  addItem: (token, data) => api.post(`/public/lists/${token}/items`, data),
  updateItem: (token, itemId, data) => api.put(`/public/lists/${token}/items/${itemId}`, data),
  deleteItem: (token, itemId) => api.delete(`/public/lists/${token}/items/${itemId}`),
};
